/* eslint-disable react/prop-types */
import { Badge } from '@components/common/Badge';
import { Table } from '@radix-ui/themes';
import { RxCheck, RxCross1 } from 'react-icons/rx';
import { Filter } from 'lucide-react';
import PropTypes from 'prop-types';
import { memo, useState, useRef, useEffect } from 'react';
import { cn } from '@helpers/styleUtils';
import { HoverableTableSkeleton } from '@components/loaders/SkeletonLoaders';
import { format } from 'date-fns';
import { Input } from '@components/common/Input';
import { MultiSelect } from '@components/common/MultiSelect';

import { JobReviewForm } from './JobReviewForm';
import { ScrollArea } from '@components/common/ScrollArea';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { DATA_STORE_KEYS, DataStore } from '@providers/dataStore';

export const JobRequestsTable = memo(
    ({
        hideAction,
        hideSelector,
        jobRequests,
        loading,
        jobRequestDataSelected,
        handleReviewOldFlow,
        setJobRequestDataSelected,
        showDetailedView,
        isDetailedView,
        handleApprove,
        handleReject,
        handleReview,
        sortConfig,
        onSort,
        externalFilters,
        onExternalFiltersChange,
    }) => {
        const [isFormOpen, setIsFormOpen] = useState(false);
        const [selectedJobRequest, setSelectedJobRequest] = useState(null);
        const [activeColumn, setActiveColumn] = useState(null);
        const [inputValues, setInputValues] = useState({}); // Add this new state
        const inputRefs = useRef({});

        const statusOptions = [
            { label: 'All', value: '' },
            { label: 'Pending', value: 'Pending' },
            { label: 'Approved', value: 'Approved' },
            { label: 'Cancel', value: 'Cancel' },
            { label: 'Completed', value: 'Completed' },
        ];
        const [wfmJobReviewData, setWfmJobReviewData] = useState({
            target_date: '',
            department: '',
            team_leader_headcount_required: '',
            shift_pattern: '',
            working_hours_start_time: '',
            working_hours_end_time: '',
            vacancy_reason: '',
            type_of_employment: '',
            duration_of_employment: '',
            role_budget_Salary: '',
            sow_change_order_received: '',
        });
        const [headerFilters, setHeaderFilters] = useState({
            location: '',
            requested_by_name: '',
            position_required: '',
            CampaignName: '',
            status: [],
            is_Amended: '',
            Induction_Start_date: '',
        });

        // Sync with external filters
        useEffect(() => {
            if (externalFilters) {
                setHeaderFilters(prev => ({
                    ...prev,
                    location: externalFilters.location || '',
                    requested_by_name: externalFilters.requestedBy || '',
                    CampaignName: externalFilters.campaignName || '',
                    status: externalFilters.status || [],
                }));
            }
        }, [externalFilters]);

        const handleHeaderFilterChange = (field, value) => {
            console.log('Header filter changed:', field, value);
            setHeaderFilters(prev => ({
                ...prev,
                [field]: value,
            }));
            setInputValues(prev => ({
                ...prev,
                [field]: value,
            }));

            // Sync with external filters if available
            if (onExternalFiltersChange) {
                const fieldMapping = {
                    location: 'location',
                    requested_by_name: 'requestedBy',
                    CampaignName: 'campaignName',
                    status: 'status',
                };

                const externalField = fieldMapping[field];
                if (externalField) {
                    onExternalFiltersChange(prev => ({
                        ...prev,
                        [externalField]: value,
                    }));
                }
            }
        };

        const handleReviewClick = (e, item) => {
            e.stopPropagation();
            if (item.is_wfm !== 'yes') {
                handleReviewOldFlow({
                    requestId: item.ID,
                    tableName: item.table_name,
                    action: 'Okay',
                });
                return;
            }
            setSelectedJobRequest(item);
            setIsFormOpen(true);
        };

        const handleSubmitClick = async () => {
            await handleReview({
                requestId: selectedJobRequest.ID,
                tableName: selectedJobRequest.table_name,
                action: 'Okay',
                wfmFormData: wfmJobReviewData,
            });
            setIsFormOpen(false);
        };

        const getSortIcon = field => {
            if (sortConfig.field !== field) return null;
            return sortConfig.direction === 'asc' ? (
                <ArrowUp className="h-4 w-4" />
            ) : (
                <ArrowDown className="h-4 w-4" />
            );
        };

        const SortableHeader = ({ field, children }) => (
            <Table.ColumnHeaderCell className="border-r border-gray-200 p-3 text-left">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                        <div
                            className="flex items-center gap-2 cursor-pointer lg:text-xs xl:text-sm"
                            onClick={() => onSort(field)}
                        >
                            {children}
                            {getSortIcon(field)}
                        </div>
                        {field !== 'ID' && (
                            <div className="flex items-center gap-2">
                                {((Array.isArray(headerFilters[field]) &&
                                    headerFilters[field].length > 0) ||
                                    (!Array.isArray(headerFilters[field]) &&
                                        headerFilters[field])) && (
                                    <RxCross1
                                        className="h-4 w-4 cursor-pointer hover:text-red-500 font-bold"
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleHeaderFilterChange(
                                                field,
                                                field === 'status' ? [] : '',
                                            );
                                        }}
                                    />
                                )}
                                <Filter
                                    className={cn(
                                        'h-4 w-4 cursor-pointer hover:text-primary-blue',
                                        (activeColumn === field ||
                                            (Array.isArray(headerFilters[field]) &&
                                                headerFilters[field].length > 0) ||
                                            (!Array.isArray(headerFilters[field]) &&
                                                headerFilters[field])) &&
                                            'text-primary-blue',
                                    )}
                                    onClick={e => {
                                        e.stopPropagation();
                                        const newActiveColumn =
                                            field === activeColumn ? null : field;
                                        setActiveColumn(newActiveColumn);
                                        if (newActiveColumn && inputRefs.current[field]) {
                                            setTimeout(() => {
                                                inputRefs.current[field]?.focus();
                                            }, 0);
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    {activeColumn === field && field !== 'ID' && (
                        <div onClick={e => e.stopPropagation()} className="relative">
                            {' '}
                            {field === 'status' ? (
                                <MultiSelect
                                    className="h-8 text-sm"
                                    value={headerFilters[field]}
                                    onChange={value => handleHeaderFilterChange(field, value)}
                                    onFocus={() => setActiveColumn(field)}
                                    options={statusOptions}
                                    placeholder="Statuses..."
                                />
                            ) : (
                                <Input
                                    ref={el => (inputRefs.current[field] = el)}
                                    className="h-8 text-sm"
                                    placeholder={`Filter ${field}...`}
                                    value={inputValues[field] || ''}
                                    onChange={e => {
                                        handleHeaderFilterChange(field, e.target.value);

                                        // Set focus back to the input after state update
                                        setTimeout(() => {
                                            if (inputRefs.current[field]) {
                                                inputRefs.current[field].focus();
                                                // Place cursor at the end of input
                                                const len = e.target.value.length;
                                                inputRefs.current[field].setSelectionRange(
                                                    len,
                                                    len,
                                                );
                                            }
                                        }, 0);
                                    }}
                                    onFocus={() => setActiveColumn(field)}
                                />
                            )}
                        </div>
                    )}
                </div>
            </Table.ColumnHeaderCell>
        );

        // Filter the job requests based on header filters
        const filteredJobRequests = jobRequests.filter(item => {
            return Object.entries(headerFilters).every(([key, value]) => {
                if (!value || (Array.isArray(value) && value.length === 0)) return true;

                // Handle array values (multiple selection)
                if (Array.isArray(value)) {
                    const itemValue = String(item[key] || '');
                    return value.includes(itemValue);
                }

                // Handle string values (single selection)
                const itemValue = String(item[key] || '').toLowerCase();
                return itemValue.includes(value.toLowerCase());
            });
        });

        useEffect(() => {
            const handleClickOutside = event => {
                // Skip if there's no active column
                if (!activeColumn) return;

                // Get the current input element and its container
                const activeInput = inputRefs.current[activeColumn];
                if (!activeInput) return;

                // Check if click is within the current active column's container
                const clickedElement = event.target;
                const isClickInsideInput = activeInput.contains(clickedElement);
                const isClickInsideFilter = clickedElement.closest('.h-4.w-4.cursor-pointer');

                // Only close if click is outside both input and filter icon
                if (!isClickInsideInput && !isClickInsideFilter) {
                    // Keep the column active if there's a value in the filter
                    const filterValue = headerFilters[activeColumn];
                    const hasValue = Array.isArray(filterValue)
                        ? filterValue.length > 0
                        : !!filterValue;
                    if (!hasValue) {
                        setActiveColumn(null);
                    }
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [activeColumn, headerFilters]);

        if (loading) {
            return <HoverableTableSkeleton rows={4} columns={4} showHeader={true} />;
        }

        const isWfm = DataStore.getItem(DATA_STORE_KEYS.USER_CURRENT_ROLE).toLowerCase() === 'wfm';

        return (
            <>
                <div className="overflow-x-auto table-auto w-full border border-gray-200 bg-white rounded-[10px]">
                    <ScrollArea className="h-[400px]">
                        <Table.Root layout="auto">
                            <Table.Header className="bg-white w-full">
                                <Table.Row className="text-sm font-semibold border-b border-gray-200">
                                    {!hideSelector && (
                                        <Table.ColumnHeaderCell className="border-r border-gray-200 p-3 text-left">
                                            <input className="w-full h-full" type="checkbox" />
                                        </Table.ColumnHeaderCell>
                                    )}
                                    <Table.ColumnHeaderCell>ID:</Table.ColumnHeaderCell>
                                    <SortableHeader field="location">Location:</SortableHeader>
                                    <SortableHeader field="requested_by_name">
                                        Requested By:
                                    </SortableHeader>
                                    <SortableHeader field="position_required">
                                        Requests Headline:
                                    </SortableHeader>
                                    <SortableHeader
                                        field="CampaignName"
                                        className="lg:font-xs xl:font-md"
                                    >
                                        Campaign Name:
                                    </SortableHeader>
                                    <SortableHeader field="status">Status:</SortableHeader>
                                    <SortableHeader field="is_Amended">Is Amended:</SortableHeader>
                                    <SortableHeader field="Induction_Start_date">
                                        Induction Date:
                                    </SortableHeader>
                                    {!hideAction && (
                                        <Table.ColumnHeaderCell className="p-3 text-left min-w-24"></Table.ColumnHeaderCell>
                                    )}
                                </Table.Row>
                            </Table.Header>
                            {filteredJobRequests.length === 0 ? (
                                <Table.Body>
                                    <Table.Row>
                                        <Table.Cell colSpan={9} className="text-center py-4">
                                            <p className="text-sm font-semibold">
                                                No job requests found.
                                            </p>
                                        </Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            ) : (
                                <Table.Body className="divide-y divide-gray-200">
                                    {filteredJobRequests.map(item => (
                                        <Table.Row
                                            key={item.ID}
                                            className={cn(
                                                'hover:bg-slate-100 bg-white cursor-pointer',
                                                jobRequestDataSelected &&
                                                    jobRequestDataSelected.ID === item.ID
                                                    ? 'bg-slate-100'
                                                    : '',
                                            )}
                                            onClick={() => {
                                                setJobRequestDataSelected(item);
                                                isDetailedView && showDetailedView(true);
                                            }}
                                        >
                                            <Table.Cell className="border-r border-gray-200 p-3 text-left text-sm">
                                                {item.ID}
                                            </Table.Cell>
                                            <Table.Cell className="border-r border-gray-200 p-3 text-left text-sm">
                                                {item.location}
                                            </Table.Cell>
                                            <Table.Cell className="border-r border-gray-200 p-3 text-left text-sm">
                                                {item.requested_by_name}
                                            </Table.Cell>
                                            <Table.RowHeaderCell className="border-r border-gray-200 p-3 text-left text-sm font-semibold">
                                                {item.position_required}
                                            </Table.RowHeaderCell>
                                            <Table.RowHeaderCell className="border-r border-gray-200 p-3 text-left text-sm font-semibold">
                                                {item.CampaignName}
                                            </Table.RowHeaderCell>

                                            <Table.Cell className="border-r border-gray-200 p-3 text-left ">
                                                <Badge
                                                    className="min-w-20 text-center"
                                                    variant={
                                                        item.status === 'Pending'
                                                            ? 'warning'
                                                            : item.status === 'Approved'
                                                            ? 'success'
                                                            : item.status === 'Completed'
                                                            ? 'completed'
                                                            : 'danger'
                                                    }
                                                >
                                                    {item.status}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell className="border-r border-gray-200 p-3 text-left text-sm">
                                                {item.is_Amended === 'Amended' ? 'Amended' : ''}{' '}
                                                {/* Added is_Amended */}
                                            </Table.Cell>
                                            <Table.Cell className="border-r border-gray-200 p-3 text-left text-sm ">
                                                {item?.Induction_Start_date
                                                    ? format(
                                                          new Date(item.Induction_Start_date),
                                                          'MMM, d, yyyy',
                                                      )
                                                    : 'N/A'}
                                            </Table.Cell>
                                            {!hideAction && item.user_type === 'director' && (
                                                <Table.Cell className="flex items-center justify-center gap-2 p-3 text-left text-sm ">
                                                    <div
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            handleApprove();
                                                        }}
                                                        className="rounded-full px-2 py-1 bg-green-600 text-white flex items-center justify-center gap-1"
                                                    >
                                                        <RxCheck className="text-lg" /> Approve
                                                    </div>
                                                    <div
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            handleReject();
                                                        }}
                                                        className="rounded-full px-2 py-1 bg-red-600 text-white flex items-center justify-center gap-1"
                                                    >
                                                        <RxCross1 className="text-base" />
                                                        Reject
                                                    </div>
                                                </Table.Cell>
                                            )}

                                            {!hideAction &&
                                                !isWfm &&
                                                item.user_type === 'recruiter' &&
                                                item.reviewed_at === null && (
                                                    <Table.Cell className="flex items-center justify-center gap-2 p-3 text-left text-sm ">
                                                        {item.status === 'Pending' && (
                                                            <div
                                                                onClick={e =>
                                                                    handleReviewClick(e, item)
                                                                }
                                                                className="rounded-full px-2 py-1 bg-primary-blue text-white flex items-center justify-center gap-1"
                                                            >
                                                                <RxCheck className="text-lg" />{' '}
                                                                Review
                                                            </div>
                                                        )}
                                                    </Table.Cell>
                                                )}
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            )}
                        </Table.Root>
                    </ScrollArea>
                </div>

                <JobReviewForm
                    wfmJobReviewData={wfmJobReviewData}
                    handleOnSubmit={handleSubmitClick}
                    setWfmJobReviewData={setWfmJobReviewData}
                    isOpen={isFormOpen}
                    onClose={() => setIsFormOpen(false)}
                />
            </>
        );
    },
);

JobRequestsTable.displayName = 'JobRequestsTable';

JobRequestsTable.propTypes = {
    hideAction: PropTypes.bool,
    showDetailedView: PropTypes.func,
    isDetailedView: PropTypes.bool,
    hideSelector: PropTypes.bool,
    handleApprove: PropTypes.func,
    handleReject: PropTypes.func,
    handleReview: PropTypes.func,
    jobRequests: PropTypes.arrayOf(
        PropTypes.shape({
            ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            position_required: PropTypes.string.isRequired,
            job_description: PropTypes.string.isRequired,
            status: PropTypes.string.isRequired,
            Induction_Start_date: PropTypes.string.isRequired,
        }),
    ).isRequired,
    loading: PropTypes.bool,
    jobRequestDataSelected: PropTypes.shape({
        ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
    setJobRequestDataSelected: PropTypes.func,
    sortConfig: PropTypes.shape({
        field: PropTypes.string.isRequired,
        direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
    }),
    onSort: PropTypes.func.isRequired,
    externalFilters: PropTypes.object,
    onExternalFiltersChange: PropTypes.func,
};
